import Autocomplete, {
   AutocompleteProps, AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import { observer } from 'mobx-react-lite'
import React, { useCallback } from 'react'
import useStore from 'reusable/mobx-store-utils'
import TextFieldCustom from '../text-field/TextFieldCustom'
import { AutocompleteOption } from './shared/AutocompleteOption'

export interface AutocompleteSingleProps
   extends Omit<
      AutocompleteProps<AutocompleteOption, undefined, undefined, undefined>,
      | 'multiple'
      | 'freeSolo'
      | 'disableClearable'
      | 'onChange'
      | 'value'
      | 'renderInput'
      | 'getOptionLabel'
   > {
   // omit onChange and value and define our own versions below
   onChange?: (value: any) => any
   value?: string
   label?: string
   helperText?: string
   error?: boolean
   variant?: React.ComponentProps<typeof TextField>['variant']
   autoFocus?: boolean
   includeChipTextInSelectedItem?: boolean
   /** this array of options can be passed, so that if the selected id is obsolete, probably it won't be in the
    * `options` prop, so we can still display it as selected. But the user won't be able to make a new selection
    * from items in `obsoleteOptions`, only from `options`.
    */
   obsoleteOptions?: readonly AutocompleteOption[]
}

/**
 * This exports a custom AutocompleteSingle component which wraps the MUI Autocomplete
 * @deprecated use GapAutocompleteSingle instead please :)
 */
export default observer(function AutocompleteSingle(props: AutocompleteSingleProps) {
   // This just separates out the onChange function and the value property explicitly
   // while spreading the rest of props into a componentProps variable
   const { onChange, label, error, helperText, variant, autoFocus, autoHighlight, autoSelect, onKeyDown, ...componentProps } = props

   const localStore = useStore(sp => ({
      get optionsAndValue(): {
         value: AutocompleteOption | null,
         options: readonly AutocompleteOption[],
      } {
         // value needs an initial value other than undefined otherwise MUI assumes that automcomplete is an uncontrolled component and give this error:
         // A component is changing the uncontrolled value state of Autocomplete to be controlled.
         // ref: https://stackoverflow.com/questions/70587660/autocomplete-gives-red-warning-when-value-is-set-to-the-usestate-that-it-modifie/70587700
         let value = sp.props.options.find(i => i.id === sp.props.value) || null
         let options = sp.props.options
         if (value === null && sp.props.obsoleteOptions) {
            value = sp.props.obsoleteOptions.find(i => i.id === sp.props.value) || null
            if (value !== null) {
               options = [value, ...options]
            }
         }

         return { value, options }
      },
      getOptionLabel(option: AutocompleteOption) {
         if (sp.props.includeChipTextInSelectedItem && option.chip) {
            return `${option.label} ${option.chip.label}`
         }
         return option.label
      },
      componentOnChange: (event: React.SyntheticEvent, value: any) => {
         // Check to see if an onChange event handler exists, then calls it with the
         // selected value id or undefined
         if (onChange) {
            onChange(value?.id || value || '')
         }
      }
   }), { props })

   const renderInput = useCallback((params: AutocompleteRenderInputParams) => {
      return <>
         <TextFieldCustom
            {...params}
            autoFocus={autoFocus}
            helperText={helperText}
            error={error}
            label={label}
            variant={variant}
            onKeyDown={onKeyDown}
         />
      </>
   }, [autoFocus, error, helperText, label, onKeyDown, variant])

   const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement>, option: AutocompleteOption) => {
      return option.label &&
         <Box component="li" {...props} data-id={option.id} key={option.id}>
            {option.label}
            {
               !!option.chip &&
               <Chip sx={{ ml: 2 }} size='small' label={option.chip.label} color={option.chip.color} />
            }
         </Box>
   }, [])

   return (
      <Autocomplete<AutocompleteOption, undefined, undefined, undefined>
         {...componentProps}
         options={localStore.optionsAndValue.options}
         value={localStore.optionsAndValue.value}
         onChange={localStore.componentOnChange}
         autoHighlight={typeof autoHighlight === "undefined" ? true : autoHighlight}
         autoSelect={typeof autoSelect === "undefined" ? true : autoSelect}
         getOptionLabel={localStore.getOptionLabel}
         renderInput={renderInput}
         renderOption={renderOption}
      />
   )
})
